import React from 'react'
import PropTypes from 'prop-types'
import './SuperCutoff.css'

const LABEL_PROCESSED = 'processed'
const LABEL_REMAINING = 'remaining'
const LABEL_ERRORS = 'errors'
const LABEL_LAST_UPDATED = 'Last updated:'
const ARIA_LABEL_BARRA = 'Closure progress'

/**
 * Calcola la percentuale di completamento, con clamp [0, 100] e guard contro NaN.
 * Arrotondata a un decimale per coerenza con il valore restituito dall'API
 * (campo progress_percentage, es. 46.1).
 * @param {number} processed - Budget processati con successo
 * @param {number} total     - Totale budget da processare
 * @returns {number} Percentuale con un decimale compresa tra 0 e 100
 */
const calcolaPercentuale = (processed, total) => {
  if (!total || !isFinite(total) || total <= 0) return 0
  return Math.min(100, Math.max(0, Math.round((processed / total) * 1000) / 10))
}

/**
 * Barra di progresso per il monitoraggio della coda SuperCutoff.
 * Componente puramente presentazionale: mostra percentuale, contatori
 * (processed / remaining / errors) e timestamp dell'ultimo refresh.
 *
 * Il bottone di Refresh non e responsabilita di questo componente:
 * viene renderizzato dal parent (StepProgress) per garantire una posizione
 * coerente del bottone in tutti gli stati del wizard, inclusi quelli in cui
 * la progress bar non viene renderizzata (es. waiting state).
 *
 * Props:
 *   total       {number}    Totale budget da processare
 *   processed   {number}    Budget gia processati con successo
 *   remaining   {number}    Budget ancora in coda
 *   errors      {number}    Budget andati in errore
 *   lastUpdated {string}    [opzionale] Timestamp dell'ultimo refresh (stringa formattata)
 */
const SuperCutoffProgressBar = ({
  total,
  processed,
  remaining,
  errors,
  lastUpdated,
}) => {
  const percentage = calcolaPercentuale(processed, total)
  const hasErrors = errors > 0

  return (
    <div className="supercutoff-progress-wrapper">
      <div className="supercutoff-progress-header">
        <span className="supercutoff-progress-label">
          Closure progress — {processed} / {total} budgets
        </span>
        <span className="supercutoff-progress-pct">{percentage.toFixed(1)}%</span>
      </div>

      <div
        className="supercutoff-progress-bar-track"
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ARIA_LABEL_BARRA}
      >
        {/* width è un valore calcolato a runtime — non esprimibile come classe statica */}
        <div
          className={`supercutoff-progress-bar-fill${hasErrors ? ' has-errors' : ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* aria-live="polite" + aria-atomic="true": gli aggiornamenti vengono annunciati
          dai lettori di schermo senza interrompere la lettura in corso */}
      <div aria-live="polite" aria-atomic="true">
        <div className="supercutoff-progress-counters">
          <span className="supercutoff-progress-counter">
            <span className="dot processed" aria-hidden="true" />
            {processed} {LABEL_PROCESSED}
          </span>
          <span className="supercutoff-progress-counter">
            <span className="dot remaining" aria-hidden="true" />
            {remaining} {LABEL_REMAINING}
          </span>
          {hasErrors && (
            <span className="supercutoff-progress-counter">
              <span className="dot errors" aria-hidden="true" />
              {errors} {LABEL_ERRORS}
            </span>
          )}
        </div>

        {lastUpdated && lastUpdated.trim() && (
          <div className="supercutoff-progress-timestamp">
            {LABEL_LAST_UPDATED} {lastUpdated}
          </div>
        )}
      </div>
    </div>
  )
}

SuperCutoffProgressBar.propTypes = {
  total: PropTypes.number.isRequired,
  processed: PropTypes.number.isRequired,
  remaining: PropTypes.number.isRequired,
  errors: PropTypes.number.isRequired,
  lastUpdated: PropTypes.string,
}

export default React.memo(SuperCutoffProgressBar)
